import { Link } from 'react-router-dom';
import { ChevronRight, Gauge } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { ChannelIcon } from '@/components/channels/ChannelIcon';
import { useChannelUplift } from '@/components/channels/PotentialGauge';
import { useShopPath } from '@/hooks/useShopPath';

/**
 * Compact row in the "Manage your channels" section — one per live channel.
 * Links straight to the channel's manage page. The subline shows the channel's
 * real Local Potential uplift (from the score resolver); channels outside the
 * score model fall back to the sync-status line.
 *
 * An auto-provisioned channel that's still `settingUp` (Google before its
 * setup automation completes) renders here too — amber dot, "Being set up"
 * subline — rather than in the discovery groups, whose enable CTAs don't
 * apply to it (NEA-6120).
 */
export function LiveChannelCard({ channel }) {
	const { shopPath } = useShopPath();
	const uplift = useChannelUplift(channel.id);
	const settingUp = Boolean(channel.settingUp);
	return (
		<Link to={shopPath(`/channels/${channel.id}/manage`)} className="group block">
			<SoftCard className="flex items-center gap-4 !p-5 transition hover:border-foreground/15 hover:shadow-sm">
				<div className="relative shrink-0">
					<ChannelIcon channelId={channel.id} src={channel.iconUrl} className="h-11 w-11" />
					<span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-card ring-2 ring-card">
						{settingUp ? (
							<span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-warning" />
						) : (
							<span className="relative flex h-2.5 w-2.5">
								<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
								<span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success" />
							</span>
						)}
					</span>
				</div>
				<div className="min-w-0 flex-1">
					<div className="truncate text-[15px] font-bold text-foreground">{channel.title}</div>
					{settingUp ? (
						<div className="mt-0.5 truncate text-xs text-muted-foreground">Being set up · no action needed</div>
					) : uplift != null ? (
						<div className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground">
							<Gauge className="h-3.5 w-3.5 shrink-0" />
							<span className="truncate">+{uplift}% local potential</span>
						</div>
					) : (
						<div className="mt-0.5 truncate text-xs text-muted-foreground">Live · syncing automatically</div>
					)}
				</div>
				<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" />
			</SoftCard>
		</Link>
	);
}
